import * as React from 'react';
import { Suspense } from 'react';
import { styled } from '@compiled/react';
import { LoadingSkeleton } from '@helpCenter/view/common/loading';
import { useIntl } from 'react-intl-next';
import * as grid from 'view/styles/grid';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import EditorSettingsIcon from '@atlaskit/icon/glyph/editor/settings';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { HELP_CENTERS_MANAGEMENT_BASE_PATH } from '@atlassian/help-center-common-component/constants';
import { MultiHcDropdownContent } from '../multi-hc-dropdown-content';
import messages from './messages';

interface Props {
    currentHcName: string;
    themeColor: string;
}
export const MultiHcDropdown: React.FC<Props> = ({ currentHcName, themeColor }) => {
    const { formatMessage } = useIntl();
    const helpCenterManagementScreenUrl = HELP_CENTERS_MANAGEMENT_BASE_PATH;
    const helpCenterManagementScreenWithNewHcCreated = `${helpCenterManagementScreenUrl}?create=true`;
    const handleClick = React.useCallback((url: string) => {
        window.location.href = url;
    }, []);

    // Number of help centers to be rendered in the dropdown and the loading skeleton
    const HELP_CENTER_LENGTH = 2;
    const renderSuspense = (helpCenterLength: number) => {
        return (
            <>
                {Array(helpCenterLength)
                    .fill(1)
                    .map((_, index) => {
                        return (
                            <Box xcss={skeletonPaddingContainer} key={`multiHCLoader_${index}`}>
                                <LoadingSkeleton
                                    height={16}
                                    backgroundColor={token('color.skeleton.subtle', colors.N20)}
                                />
                            </Box>
                        );
                    })}
            </>
        );
    };
    return (
        <DropdownMenu triggerType="button" trigger={<Box xcss={triggerStyles}>{currentHcName}</Box>}>
            <Suspense fallback={renderSuspense(HELP_CENTER_LENGTH)}>
                <MultiHcDropdownContent helpCenterLength={HELP_CENTER_LENGTH} />
            </Suspense>
            <DropdownItemGroup hasSeparator>
                <Box xcss={createManageHelpCenterContainerStyles}>
                    <DropdownItem onClick={() => handleClick(helpCenterManagementScreenUrl)}>
                        <CreateManageHelpCenterItemStyles themeColor={themeColor}>
                            <Box xcss={iconPaddingStyles}>
                                <EditorSettingsIcon label="" />
                            </Box>
                            {formatMessage(messages.manageHc)}
                        </CreateManageHelpCenterItemStyles>
                    </DropdownItem>

                    <DropdownItem
                        onClick={() => {
                            handleClick(helpCenterManagementScreenWithNewHcCreated);
                        }}
                    >
                        <CreateManageHelpCenterItemStyles themeColor={themeColor}>
                            <Box xcss={iconPaddingStyles}>
                                <EditorAddIcon label="" />
                            </Box>
                            {formatMessage(messages.createHc)}
                        </CreateManageHelpCenterItemStyles>
                    </DropdownItem>
                </Box>
            </DropdownItemGroup>
        </DropdownMenu>
    );
};

interface ThemeProps {
    themeColor: string;
}

const triggerStyles = xcss({
    maxWidth: grid.multiple(24).px,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const CreateManageHelpCenterItemStyles = styled.div<ThemeProps>({
    color: (props) => token('color.link', props.themeColor),
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
        color: (props) => token('color.link', props.themeColor),
    },
});

const iconPaddingStyles = xcss({
    paddingRight: token('space.050', '4px'),
});

const skeletonPaddingContainer = xcss({
    padding: 'space.100',
});
const createManageHelpCenterContainerStyles = xcss({ marginLeft: token('space.negative.050', '-4px') });
